import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/shared/Navbar';
import DashboardSidebar from '../components/shared/DashboardSidebar';
import Footer from '../components/shared/Footer';
import UploadedDocuments from '../components/shared/UploadedDocuments';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  FileText, Download, Eye, Clock, CheckCircle, Plus,
  AlertCircle, Loader2, Globe, Package
} from 'lucide-react';

const documentTypes = [
  { value: 'certificate_of_origin', label: 'AfCFTA Certificate of Origin' },
  { value: 'customs_declaration', label: 'Customs Declaration' },
  { value: 'commercial_invoice', label: 'Commercial Invoice' },
  { value: 'packing_list', label: 'Packing List' },
  { value: 'export_permit', label: 'Export Permit' },
];

const countries = [
  'Nigeria', 'Ghana', 'Kenya', 'South Africa', 'Egypt', 'Morocco',
  'Tanzania', 'Ethiopia', 'Rwanda', 'Senegal'
];

const DocumentsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  
  const [formData, setFormData] = useState({
    document_type: '',
    export_country: '',
    import_country: '',
    product_name: '',
    product_value: '',
    hs_code: '',
  });

  const fetchDocuments = useCallback(async () => {
    try {
      const token = localStorage.getItem('atff_token');
      const response = await axios.get(`${API}/documents`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        // withCredentials removed for CORS compatibility
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDocuments();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, fetchDocuments]);

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGenerate = async () => {
    if (!formData.document_type || !formData.export_country || !formData.import_country) {
      toast.error('Please fill in all required fields');
      return;
    }

    setGenerating(true);
    try {
      const token = localStorage.getItem('atff_token');
      await axios.post(
        `${API}/documents/generate`,
        {
          document_type: formData.document_type,
          export_country: formData.export_country,
          import_country: formData.import_country,
          product_details: {
            name: formData.product_name,
            value: parseFloat(formData.product_value) || 0,
            hs_code: formData.hs_code,
          }
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          // withCredentials removed for CORS compatibility
        }
      );
      
      toast.success('Document generated successfully!');
      setShowForm(false);
      setFormData({
        document_type: '',
        export_country: '',
        import_country: '',
        product_name: '',
        product_value: '',
        hs_code: '',
      });
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to generate document');
    } finally {
      setGenerating(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'generated':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> Generated</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className={isAuthenticated ? 'flex' : ''}>
        {/* Sidebar for authenticated users */}
        {isAuthenticated && <DashboardSidebar />}

        <div className={isAuthenticated ? 'flex-1' : ''}>
          {/* Hero */}
          <section className={`bg-charcoal text-white py-12 ${isAuthenticated ? 'mx-6 mt-6 rounded-lg' : ''}`} data-testid="documents-hero">
            <div className="container-main">
              <div className="flex items-center justify-between">
                <div>
                  <Badge className="bg-tradeGold text-black mb-4">Trade Documentation</Badge>
                  <h1 className="text-3xl md:text-4xl font-bold mb-4 font-poppins">
                    Customs & Trade Documents
                  </h1>
                  <p className="text-gray-300 max-w-xl">
                    Generate AfCFTA-compliant trade documents. Certificates of origin, customs declarations, 
                    commercial invoices, and more.
                  </p>
                </div>
                {isAuthenticated && (
                  <Button onClick={() => setShowForm(true)} className="btn-secondary hidden md:flex">
                    <Plus className="w-4 h-4 mr-2" />
                    Generate Document
                  </Button>
                )}
              </div>
            </div>
          </section>

          <div className={isAuthenticated ? 'p-6' : 'container-main py-12'}>
            {!isAuthenticated ? (
              <Card className="max-w-md mx-auto text-center">
                <CardContent className="py-12">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-charcoal mb-2">Sign in to Generate Documents</h2>
                  <p className="text-gray-500 mb-6">
                    Create an account or sign in to generate trade documents
                  </p>
                  <Button onClick={() => navigate('/login')} className="btn-primary">
                Sign In
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            <Card className="lg:col-span-3">
              <CardContent className="pt-6">
                <UploadedDocuments currentUserId={user?.user_id} />
              </CardContent>
            </Card>
            {/* Document Generation Form */}
            {showForm && (
              <div className="lg:col-span-3 mb-8">
                <Card>
                  <CardHeader>
                    <CardTitle>Generate New Document</CardTitle>
                    <CardDescription>Fill in the details to generate your trade document</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label>Document Type *</Label>
                        <Select value={formData.document_type} onValueChange={(v) => handleChange('document_type', v)}>
                          <SelectTrigger className="mt-1" data-testid="doc-type-select">
                            <SelectValue placeholder="Select document type" />
                          </SelectTrigger>
                          <SelectContent>
                            {documentTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>HS Code</Label>
                        <Input
                          value={formData.hs_code}
                          onChange={(e) => handleChange('hs_code', e.target.value)}
                          placeholder="e.g., 1801.00"
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label>Export Country *</Label>
                        <Select value={formData.export_country} onValueChange={(v) => handleChange('export_country', v)}>
                          <SelectTrigger className="mt-1" data-testid="export-country-select">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent>
                            {countries.map((c) => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Import Country *</Label>
                        <Select value={formData.import_country} onValueChange={(v) => handleChange('import_country', v)}>
                          <SelectTrigger className="mt-1" data-testid="import-country-select">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent>
                            {countries.filter(c => c !== formData.export_country).map((c) => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Product Name</Label>
                        <Input
                          value={formData.product_name}
                          onChange={(e) => handleChange('product_name', e.target.value)}
                          placeholder="e.g., Cocoa Beans"
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label>Product Value (USD)</Label>
                        <Input
                          type="number"
                          value={formData.product_value}
                          onChange={(e) => handleChange('product_value', e.target.value)}
                          placeholder="e.g., 50000"
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                      <Button variant="outline" onClick={() => setShowForm(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleGenerate} className="btn-primary" disabled={generating}>
                        {generating ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            Generate Document
                            <FileText className="w-4 h-4 ml-2" />
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Document Types */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Available Documents</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {documentTypes.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => {
                        setShowForm(true);
                        handleChange('document_type', type.value);
                      }}
                      className="w-full flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                    >
                      <FileText className="w-5 h-5 text-continental" />
                      <span className="text-sm font-medium">{type.label}</span>
                    </button>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Documents List */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">Your Documents</CardTitle>
                  <Button onClick={() => setShowForm(true)} size="sm" className="lg:hidden">
                    <Plus className="w-4 h-4 mr-1" />
                    New
                  </Button>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-continental" />
                    </div>
                  ) : documents.length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No documents generated yet</p>
                      <Button onClick={() => setShowForm(true)} variant="outline" size="sm" className="mt-3">
                        Generate your first document
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {documents.map((doc) => (
                        <div key={doc.document_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-continental/10 rounded-lg flex items-center justify-center">
                              <FileText className="w-5 h-5 text-continental" />
                            </div>
                            <div>
                              <p className="font-medium text-charcoal">
                                {documentTypes.find(t => t.value === doc.document_type)?.label || doc.document_type}
                              </p>
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <Globe className="w-3 h-3" />
                                {doc.document_data?.export_country} → {doc.document_data?.import_country}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {getStatusBadge(doc.status)}
                            <Button variant="ghost" size="sm">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm">
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
          </div>
        </div>
      </div>

      {!isAuthenticated && <Footer />}
    </div>
  );
};

export default DocumentsPage;
